// Mirrors Angular's authService.callOAuth(): fetches a fresh OAuth Authorization
// key and stores it as `AuthorizationKey` in localStorage. baseQuery then sends
// it as the `Authorization` header on subsequent token/login requests.

const OAUTH_URL =
  'https://eeqzof4a8g.execute-api.ap-south-1.amazonaws.com/api/oauth_test';

// localStorage key holding the epoch-ms time the current AuthorizationKey must
// be refetched by. Without this the refetch manager could only tell whether the
// key was MISSING — a present-but-stale key was never refreshed, so requests
// kept 401-ing until the coarse ~55-min hard refresh. The key is treated as
// valid for 50 minutes (comfortably under the gateway's ~1h key lifetime).
const AUTH_KEY_EXPIRY_KEY = 'AuthorizationKeyExpiry';
// Treat the key as valid for 50 minutes — comfortably under the gateway's ~1h
// key lifetime so it is refetched before the server starts rejecting it.
const AUTH_KEY_TTL_MS = 50 * 60 * 1000;

/**
 * True when there is no AuthorizationKey, or when the stored expiry stamp is
 * missing/past. Any parse failure is treated as expired (fail-safe → refetch).
 */
export function isAuthKeyExpired(): boolean {
  if (!localStorage.getItem('AuthorizationKey')) return true;
  const stamp = Number(localStorage.getItem(AUTH_KEY_EXPIRY_KEY));
  if (!Number.isFinite(stamp) || stamp <= 0) return true;
  return Date.now() >= stamp;
}

/**
 * The OAuth key can arrive in several response shapes depending on the gateway:
 *   A) { Authorization_key: "..." }                    (direct)
 *   B) { body: "{\"Authorization_key\":\"...\"}" }      (lambda proxy, stringified)
 *   C) { body: { Authorization_key: "..." } }           (lambda proxy, parsed)
 * Extract the key from whichever shape is returned.
 */
function extractAuthKey(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const obj = data as Record<string, unknown>;

  if (typeof obj.Authorization_key === 'string') {
    return obj.Authorization_key;
  }

  const body = obj.body;
  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body) as Record<string, unknown>;
      if (typeof parsed?.Authorization_key === 'string') {
        return parsed.Authorization_key;
      }
    } catch {
      /* body wasn't JSON — fall through */
    }
  }
  if (body && typeof body === 'object') {
    const key = (body as Record<string, unknown>).Authorization_key;
    if (typeof key === 'string') return key;
  }

  return undefined;
}

/**
 * Calls the OAuth endpoint and stores the returned key in localStorage.
 * Returns the key on success, or null on failure.
 */
export async function fetchOAuthKey(): Promise<string | null> {
  try {
    const existing = localStorage.getItem('AuthorizationKey');
    const res = await fetch(OAUTH_URL, {
      method: 'GET',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        // Re-send the current key when present (matches the reference app).
        ...(existing ? { Authorization: existing } : {}),
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    const data = await res.json();
    const key = extractAuthKey(data);

    if (!key) {
      throw new Error('OAuth response missing Authorization_key');
    }

    localStorage.setItem('AuthorizationKey', key);
    // Stamp when this key should next be refetched, so isAuthKeyExpired() can
    // drive proactive refreshes instead of only reacting to a missing key.
    localStorage.setItem(
      AUTH_KEY_EXPIRY_KEY,
      String(Date.now() + AUTH_KEY_TTL_MS)
    );
    return key;
  } catch (err) {
    console.error('[OAuth] Failed to fetch Authorization key:', err);
    return null;
  }
}
